/**
 * @note This children component use self state, lifecycle and fetching data from store, please be careful for handling the state :)
 */

// React
import { useCallback } from 'react'

// Components
import { BaseAlert } from 'components'

// Mobx
import { observer } from 'mobx-react'

// Hook
import { useAuth } from 'hooks'

// Constant
import { USER_STATUS } from 'constant/shared.constant'

// i18n
import { useTranslation } from 'react-i18next'

// Lodash
import pick from 'lodash/pick'

// Antd
import { AlertProps } from 'antd'

// Custom Hook
const useSharedUserInfoAlert = () => {
  // Hook
  const { auth_authenticatedUser } = useAuth()
  const { t } = useTranslation()

  /**
   * @description Render alert message
   *
   * @param {USER_STATUS} status
   *
   * @return {object} Object of alert
   */
  const renderAlertMessage = useCallback((): { message: string; type: string; messages: string[] } => {
    let message = {
      message: t('auth.title.accountInformation'),
      type: 'warning',
      messages: [],
    }

    const { isPhoneNumberValidated, isProfileCompleted, status, accountStatus, isValidated, roles } = auth_authenticatedUser

    // Check for form fill
    if (!isProfileCompleted) message.messages.push(t('auth.validations.fillProfile'))

    // Check for phone number
    if (!isPhoneNumberValidated && isProfileCompleted) message.messages.push(t('auth.validations.phoneNumberNotValidated'))

    // Check if all identifier has been filled
    if (isValidated) {
      message.type = 'success'
      message.messages.push(t('auth.validations.verified'))
    }

    // Check if user not have role
    if ((!roles || roles.length === 0) && status === USER_STATUS.APPROVED) message.messages.push(t('auth.validations.roleEmpty'))

    // Check the account status
    if (!accountStatus) {
      message.type = 'error'
      message.messages.push(t('auth.validations.suspended'))
    }

    switch (status as USER_STATUS) {
      case USER_STATUS.WAITING_APPROVAL:
        message.messages.push(t('auth.validations.waitingApproval'))
        break
      case USER_STATUS.REJECTED:
        message.type = 'error'
        message.messages.push(t('auth.validations.rejected'))
        break
      default:
        return message
    }

    return message
  }, [t, auth_authenticatedUser])

  /**
   * @description Render description
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderDescription = useCallback((): JSX.Element => {
    return (
      <ul>
        {renderAlertMessage().messages.map((message, index) => (
          <li key={index} style={{ textAlign: 'justify' }}>
            {renderAlertMessage().messages.length === 1 ? '' : `${index + 1}.`} {message}
          </li>
        ))}
      </ul>
    )
  }, [renderAlertMessage])

  return { renderAlertMessage, renderDescription }
}

const SharedUserInfoAlert = observer(() => {
  // Hook
  const { renderAlertMessage, renderDescription } = useSharedUserInfoAlert()

  return <BaseAlert {...(pick(renderAlertMessage(), ['message', 'type']) as AlertProps)} description={renderDescription()} />
})

export { SharedUserInfoAlert, useSharedUserInfoAlert }
